import {
  useState,
  useEffect,
  useRef,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { XIcon } from '@phosphor-icons/react';
import { pillStylesBase, pillVariants } from './base-pill-styles';

interface PillInputProps {
  values?: string[];
  placeholder?: string;
  onChange?: (values: string[]) => void;
  className?: string;
}

export function PillInput({
  placeholder = 'Type and press Enter or comma to add...',
  onChange,
  className = '',
  values: pills = [],
}: PillInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingIndex]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Check for Escape key to cancel editing
    if (e.key === 'Escape') {
      setEditingIndex(null);
      setInputValue('');
      return;
    }

    // Check for Enter key or comma
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();

      if (editingIndex !== null) {
        // Update existing pill
        updatePill();
      } else {
        // Add new pill
        addPill();
      }
    } else if (e.key === 'Backspace' && inputValue === '' && pills.length > 0) {
      // If in edit mode, exit edit mode when backspace is pressed on empty input
      if (editingIndex !== null) {
        setEditingIndex(null);
      } else {
        // Remove the last pill when backspace is pressed and input is empty
        const newPills = [...pills];
        newPills.pop();
        onChange?.(newPills);
      }
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // If the user types a comma, add a pill
    if (value.includes(',')) {
      setInputValue('');
      const pillValue = value.replace(',', '').trim();
      if (pillValue) {
        addPillWithValue(pillValue);
      }
    } else {
      setInputValue(value);
    }
  };

  const addPill = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      addPillWithValue(trimmedValue);
      setInputValue('');
    }
  };

  const addPillWithValue = (value: string) => {
    if (value && !pills.includes(value)) {
      const newPills = [...pills, value];
      onChange?.(newPills);
    }
  };

  const removePill = (index: number) => {
    const newPills = pills.filter((_, i) => i !== index);
    onChange?.(newPills);
  };

  const updatePill = () => {
    const trimmedValue = inputValue.trim();
    if (editingIndex !== null && trimmedValue) {
      // Check if the new value already exists in another pill
      const valueExistsAtDifferentIndex =
        pills.findIndex(
          (pill, index) => pill === trimmedValue && index !== editingIndex,
        ) !== -1;

      if (!valueExistsAtDifferentIndex) {
        const newPills = [...pills];
        newPills[editingIndex] = trimmedValue;
        onChange?.(newPills);
      }

      setEditingIndex(null);
      setInputValue('');
    }
  };

  const editPill = (index: number) => {
    setEditingIndex(index);
    setInputValue(pills[index]);
  };

  return (
    <div
      className={`${pillStylesBase.container} p-2 border rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:border-input ${className}`}
    >
      {pills.map((pill, index) =>
        editingIndex === index ? null : (
          <div key={index} className={pillVariants({ variant: 'clickable' })}>
            <span onClick={() => editPill(index)}>{pill}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removePill(index);
              }}
              className={pillStylesBase.removeButton}
              aria-label={`Remove ${pill}`}
            >
              <XIcon className={pillStylesBase.removeIcon} />
            </button>
          </div>
        ),
      )}
      <input
        ref={inputRef}
        type="text"
        className="flex-1 min-w-[120px] bg-transparent outline-none"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // When clicking outside, if in edit mode, update the pill
          if (editingIndex !== null) {
            updatePill();
          }
        }}
        placeholder={pills.length === 0 ? placeholder : ''}
      />
    </div>
  );
}
